/**
 * Graph query results cached in IndexedDB (spec §15.8): keyed by `(crdtDocId, treeId,
 * sha256(query))` for `graph_view` blocks, so an offline view shows the last result with a stale
 * marker. `hash-wasm` instead of WebCrypto so it also works outside secure contexts.
 */
import { sha256 } from "hash-wasm";
import { ApiError, api, unwrap } from "../api/client";
import type { QueryResult } from "../api/hooks";
import { db } from "../sync/persistence";

export interface CachedRun {
  result: QueryResult;
  fetchedAt: number;
  stale: boolean;
  error?: string;
}

export async function queryCacheKey(...parts: string[]): Promise<string> {
  const last = parts.pop() ?? "";
  return [...parts, await sha256(last)].join(":");
}

export async function readCache(key: string): Promise<{ result: QueryResult; fetchedAt: number } | undefined> {
  const row = await (await db()).get("queryCache", key);
  return row ? { result: row.result as QueryResult, fetchedAt: row.fetchedAt } : undefined;
}

export async function writeCache(key: string, result: QueryResult): Promise<void> {
  await (await db()).put("queryCache", { key, result, fetchedAt: Date.now() });
}

export async function runGraphQuery(workspaceId: string, query: string, maxRows?: number): Promise<QueryResult> {
  return unwrap<QueryResult>(await api.POST("/api/graph/query", { body: { workspaceId, query, maxRows: maxRows ?? null } }));
}

/** Run live; on a network or server failure fall back to the cached result (marked stale).
 *  Client errors (syntax, 403) are never masked by a cached result. */
export async function runWithCache(workspaceId: string, query: string, key: string | null): Promise<CachedRun> {
  try {
    const result = await runGraphQuery(workspaceId, query);
    if (key) await writeCache(key, result).catch(() => {});
    return { result, fetchedAt: Date.now(), stale: false };
  } catch (e) {
    if (e instanceof ApiError && e.status < 500) throw e;
    const cached = key ? await readCache(key).catch(() => undefined) : undefined;
    if (cached) return { ...cached, stale: true, error: e instanceof Error ? e.message : String(e) };
    throw e;
  }
}
